import { useEffect, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { CameraView, useCameraPermissions } from 'expo-camera'
import { AppModal } from '@/components/ui/AppModal'
import { Button } from '@/components/ui/Button'
import { FONTS, SPACING, RADIUS } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { parseHandoverQrPayload } from '@/lib/utils/qr-matrix'

interface HandoverScanModalProps {
  visible: boolean
  onClose: () => void
  onCode: (code: string) => void
}

export function HandoverScanModal({ visible, onClose, onCode }: HandoverScanModalProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const [permission, requestPermission] = useCameraPermissions()
  const [locked, setLocked] = useState(false)

  useEffect(() => {
    if (visible) setLocked(false)
  }, [visible])

  return (
    <AppModal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={onClose}
      testID="handover-presence.scan-modal"
    >
      <View
        style={{
          flex: 1,
          backgroundColor: colors.background,
          padding: SPACING.lg,
          gap: SPACING.md,
        }}
      >
        <Text
          style={{
            fontSize: 20,
            lineHeight: 26,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
          }}
        >
          {t('handover_presence.scan_title')}
        </Text>
        <Text
          style={{
            fontSize: 14,
            lineHeight: 20,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
        >
          {t('handover_presence.scan_body')}
        </Text>
        {permission?.granted ? (
          <View style={{ flex: 1, borderRadius: RADIUS.lg, overflow: 'hidden' }}>
            <CameraView
              style={{ flex: 1 }}
              facing="back"
              barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
              onBarcodeScanned={({ data }) => {
                if (locked) return
                const code = parseHandoverQrPayload(String(data ?? ''))
                if (!code) return
                setLocked(true)
                onCode(code)
                onClose()
              }}
            />
          </View>
        ) : (
          <Button
            title={t('handover_presence.scan_permission_cta')}
            onPress={() => void requestPermission()}
            testID="handover-presence.scan-permission"
          />
        )}
        <Button
          title={t('common.close')}
          variant="secondary"
          onPress={onClose}
          testID="handover-presence.scan-close"
        />
      </View>
    </AppModal>
  )
}
